import { MouseEvent, useMemo, useState } from 'react'
import classNames from 'classnames'
import CarouselNav from '@/atoms/CarouselNav'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { HeadingMD, HeadingSM, ParagraphSM } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { useHistoryContent } from './OurHistoryContent'

export const OurHistorySection = () => {
  const { t } = useTranslate('about')
  const history = useHistoryContent()
  const [activeSlide, setActiveSlide] = useState<number>(0)

  const canScrollBack = useMemo(() => {
    return activeSlide > 0 && history.length > 0
  }, [history, activeSlide])

  const canScrollForward = useMemo(() => {
    return history.length > 0 && activeSlide < history.length - 1
  }, [history, activeSlide])

  const scrollBack = () => {
    setActiveSlide(activeSlide - 1)
    const destination = document.getElementById(`slide-${activeSlide - 1}`)
    if (destination) {
      destination?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }

  const scrollForward = () => {
    setActiveSlide(activeSlide + 1)
    const destination = document.getElementById(`slide-${activeSlide + 1}`)
    if (destination) {
      destination?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const index = Number((event.target as HTMLElement).id)
    if (!isNaN(index)) {
      setActiveSlide(index)
      const destination = document.getElementById(`slide-${index}`)
      if (destination) {
        destination?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      }
    }
  }

  return (
    <section className="bg-core-gray-900 py-[131px] text-center" id="history">
      <HeadingSM className="photon-heading-xl mx-auto max-w-[240px] pb-8 md:max-w-none" weight="bold">
        {t('historyOfAngel', 'History of Angel')}
      </HeadingSM>
      <div className="relative">
        <div className="flex w-full snap-x snap-mandatory gap-8 overflow-x-scroll overscroll-x-contain scrollbar-none">
          {history.map((data, index) => {
            const slideActive = activeSlide === index
            return (
              <div
                className={classNames('h-[1100px] md:h-[800px]', {
                  'ml-24': index === 0,
                  'mr-24': index === history.length - 1,
                })}
                id={`slide-${index}`}
                key={index}
              >
                <div
                  className={classNames(
                    'snap-center relative mx-auto h-[170px] w-[240px] overflow-hidden rounded-2xl md:h-[330px] md:w-[650px] xl:basis-1/2 scale-100',
                    {
                      'scale-75 lg:scale-50': !slideActive,
                    },
                  )}
                  onClick={handleClick}
                >
                  <Image
                    alt={data.image.alt}
                    fill
                    id={`${index}`}
                    loading="lazy"
                    priority={false}
                    src={data.image.src}
                    style={{ objectFit: 'cover' }}
                  />
                </div>
                <FlexColumn
                  aria-colindex={index}
                  className={classNames(
                    'mx-auto gap-8 md:px-8 pt-8 md:w-[693px] xl:mx-auto xl:w-fit xl:max-w-[1024px] xl:flex-row',
                    { hidden: !slideActive },
                  )}
                >
                  <HeadingMD className="photon-display text-left xl:my-auto" weight="bold">
                    {data.year}
                  </HeadingMD>
                  <ParagraphSM className="text-left">{data.body}</ParagraphSM>
                </FlexColumn>
              </div>
            )
          })}
        </div>
        <CarouselNav
          active={canScrollBack}
          className="top-0 p-2"
          direction="back"
          onClick={scrollBack}
          orientation="horizontal"
        />

        <CarouselNav
          active={canScrollForward}
          className="top-0 p-2"
          direction="forward"
          onClick={scrollForward}
          orientation="horizontal"
        />
      </div>
    </section>
  )
}
